import type {
  SearchFilterCondition,
  SearchFilterField,
  SearchFilterStrategy,
} from 'lib-search-types';
import {
  filterOperatorCatalogue,
  isDateFilterField,
  isLeafFilterCondition,
  isExistenceFilterOperator,
} from 'lib-search-ui';
import { ampli } from 'lib-shared-tracking';
import { PropsWithChildren } from 'lib-shared-types-utils';
import mapKeys from 'lodash/mapKeys';
import noop from 'lodash/noop';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useList, useMap, useUpdateEffect } from 'react-use';

import { useAppSelector } from 'utils/redux/useAppSelector';

import {
  filtersInitialState,
  filterValidationErrors,
  quickFilterFields,
} from './utils/definitions';

/** Represents the options arg for copying a condition. */
type CopyConditionOptions = {
  fullCopy: boolean;
};

export type SearchFiltersValidationErrorKey =
  keyof typeof filterValidationErrors;

/** Represents the filters validation state. */
type SearchFiltersValidation = {
  errors: Record<number, SearchFiltersValidationErrorKey>;
  isEmpty: boolean;
  isValid: boolean;
  validate: () => boolean;
};

/** Represents the props that must be passed to the provider. */
type SearchFiltersProviderProps = PropsWithChildren;

/** Represents the shape of the context value. */
type SearchFiltersContextType = {
  strategy: SearchFilterStrategy;
  filters: SearchFilterCondition<'conversations'>[];
  activeCount: number;
  advancedActiveCount: number;
  validation: SearchFiltersValidation;
  addCondition: (condition: SearchFilterCondition<'conversations'>) => void;
  updateCondition: (
    index: number,
    condition: SearchFilterCondition<'conversations'>,
  ) => void;
  copyCondition: (
    index: number,
    condition: SearchFilterCondition<'conversations'>,
    options: CopyConditionOptions,
  ) => void;
  removeCondition: (index: number) => void;
  /** Adds or replaces a condition by field name. */
  upsertConditionByField: (
    field: SearchFilterField<'conversations'>,
    condition: SearchFilterCondition<'conversations'>,
  ) => void;
  /** Removes a condition by field name. */
  removeConditionByField: (field: SearchFilterField<'conversations'>) => void;
  updateStrategy: (strategy: SearchFilterStrategy) => void;
  reset: VoidFunction;
};

/** The initial context value. */
export const initialContextValue: SearchFiltersContextType = {
  strategy: filtersInitialState.strategy,
  filters:
    filtersInitialState.filters as SearchFilterCondition<'conversations'>[],
  activeCount: 0,
  advancedActiveCount: 0,
  validation: {
    errors: {},
    isEmpty: false,
    isValid: true,
    validate: () => false,
  },
  addCondition: noop,
  updateCondition: noop,
  copyCondition: noop,
  removeCondition: noop,
  upsertConditionByField: noop,
  removeConditionByField: noop,
  updateStrategy: noop,
  reset: noop,
};

/** The context. */
export const SearchFiltersContext =
  createContext<SearchFiltersContextType>(initialContextValue);

/** The context provider. All its children will have access to its value. */
export function SearchFiltersProvider({
  children,
}: SearchFiltersProviderProps): JSX.Element {
  const filters = useAppSelector((state) => state.search.params.filters);
  const [strategy, setStrategy] = useState(filters.strategy);
  const [conditions, conditionSetters] = useList(filters.filters);
  const [errors, errorSetters] = useMap<SearchFiltersValidation['errors']>({});

  /**
   * Updates the strategy and conditions when the external state is updated,
   * e.g. when applying a saved search.
   */
  useUpdateEffect(() => {
    setStrategy(filters.strategy);
    conditionSetters.set(filters.filters);
  }, [filters.strategy, filters.filters]);

  /** Sets the new strategy and sends a tracking event */
  const updateStrategy = useCallback((strategy: SearchFilterStrategy) => {
    setStrategy(strategy);
    ampli.searchFilterStrategyUpdated({ strategy });
  }, []);

  /**
   * Checks whether a condition is valid according to its field, operator and
   * values.
   *
   * @returns {string | null} The error message if invalid, or nothing if valid
   */
  const validateCondition = useCallback(
    ({
      field,
      operator,
      values,
    }: SearchFilterCondition<'conversations'>): SearchFiltersValidationErrorKey | null => {
      if (isDateFilterField(field)) {
        if (operator === 'between') {
          const isInvalid =
            !values.length ||
            (values.length === 2 && values.some(({ value }) => !value));
          return isInvalid ? 'invalidRange' : null;
        }
        const isInvalid = !values.length;
        return isInvalid ? 'noDate' : null;
      }

      // For all other fields we just check that values are provided if the operator requires them.
      const isInvalid = !isExistenceFilterOperator(operator) && !values.length;
      return isInvalid ? 'noValue' : null;
    },
    [],
  );

  /**
   * Validates each condition and if the validator returns an error, it adds it
   * to the errors state.
   *
   * @returns {boolean} `false` if at least one condition is invalid, otherwise
   *   `true`
   */
  const validateConditions = useCallback(() => {
    const invalidConditions = conditions.filter(isLeafFilterCondition).reduce(
      (acc, condition, index) => {
        const errorKey = validateCondition(condition);
        if (errorKey) acc[index] = errorKey;
        return acc;
      },
      {} as SearchFiltersValidation['errors'],
    );

    const isValid = Object.keys(invalidConditions).length === 0;

    if (!isValid) {
      errorSetters.setAll(invalidConditions);

      ampli.searchFiltersValidationFailed({
        errors: mapKeys(invalidConditions, (_errorKey, index) => {
          const condition = conditions[parseInt(index)];
          return isLeafFilterCondition(condition) ? condition.field : undefined;
        }),
      });
    }

    return isValid;
  }, [conditions, errorSetters, validateCondition]);

  /** Adds a new condition to the state. */
  const addCondition = useCallback(
    (condition: SearchFilterCondition<'conversations'>) => {
      conditionSetters.push(condition);

      ampli.searchFilterConditionAdded({
        location: 'add_more',
        type: condition.field,
      });
    },
    [conditionSetters],
  );

  /**
   * Updates an existing condition in the state using its index value. If the
   * condition was previously invalid, and is now valid, the previous error is
   * cleared.
   */
  const updateCondition = useCallback(
    (index: number, condition: SearchFilterCondition<'conversations'>) => {
      conditionSetters.updateAt(index, condition);

      const isValid = !validateCondition(condition);
      if (isValid) errorSetters.remove(index);
    },
    [conditionSetters, errorSetters, validateCondition],
  );

  /**
   * Creates a copy of an existing condition and inserts it immediately after
   * the condition being copied. If `options.fullCopy` is `false`, only the
   * `field` is copied. If `true`, the `operator` and `values` are copied as
   * well.
   */
  const copyCondition = useCallback(
    (
      index: number,
      condition: SearchFilterCondition<'conversations'>,
      options: CopyConditionOptions,
    ) => {
      const newCondition = {
        ...condition,
        ...(!options.fullCopy
          ? {
              operator:
                filterOperatorCatalogue.conversations[condition.field]
                  .defaultOperator,
              values: [],
            }
          : {}),
      } as SearchFilterCondition<'conversations'>;
      conditionSetters.insertAt(index + 1, newCondition);

      ampli.searchFilterConditionAdded({
        location: options.fullCopy ? 'duplicate' : 'add_another',
        type: condition.field,
      });
    },
    [conditionSetters],
  );

  /**
   * Removes a condition from the state by its index value and clears any
   * validation errors.
   */
  const removeCondition = useCallback(
    (index: number) => {
      const condition = conditions[index];

      if (isLeafFilterCondition(condition)) {
        ampli.searchFilterConditionRemoved({ type: condition.field });
      }

      conditionSetters.removeAt(index);
      errorSetters.remove(index);
    },
    [conditions, conditionSetters, errorSetters],
  );

  /** Adds or replaces a condition by field name. */
  const upsertConditionByField = useCallback(
    (
      field: SearchFilterField<'conversations'>,
      condition: SearchFilterCondition<'conversations'>,
    ) => {
      const index = conditions.findIndex(
        (c) => isLeafFilterCondition(c) && c.field === field,
      );

      if (index >= 0) {
        conditionSetters.updateAt(index, condition);
      } else {
        conditionSetters.push(condition);
      }
    },
    [conditions, conditionSetters],
  );

  /** Removes a condition by field name. */
  const removeConditionByField = useCallback(
    (field: SearchFilterField<'conversations'>) => {
      const index = conditions.findIndex(
        (c) => isLeafFilterCondition(c) && c.field === field,
      );

      if (index >= 0) {
        conditionSetters.removeAt(index);
        errorSetters.remove(index);
      }
    },
    [conditions, conditionSetters, errorSetters],
  );

  /** Resets everything to the initial state. */
  const reset = useCallback(() => {
    setStrategy(filtersInitialState.strategy);
    conditionSetters.set(filtersInitialState.filters);
    errorSetters.reset();
  }, [conditionSetters, errorSetters]);

  const activeFilters = filters.filters;
  const activeCount = activeFilters.length;

  const advancedActiveCount = useMemo(() => {
    const seen = new Set<string>();

    return activeFilters.filter((c) => {
      if (!isLeafFilterCondition(c)) return true;

      if (quickFilterFields.has(c.field) && !seen.has(c.field)) {
        seen.add(c.field);
        return false;
      }

      return true;
    }).length;
  }, [activeFilters]);

  const contextValue: SearchFiltersContextType = useMemo(
    () => ({
      strategy,
      filters: conditions.filter(isLeafFilterCondition),
      activeCount,
      advancedActiveCount,
      validation: {
        errors,
        isEmpty: !conditions.length,
        isValid: !Object.keys(errors).length,
        validate: validateConditions,
      },
      addCondition,
      updateCondition,
      copyCondition,
      removeCondition,
      upsertConditionByField,
      removeConditionByField,
      updateStrategy,
      reset,
    }),
    [
      activeCount,
      addCondition,
      advancedActiveCount,
      conditions,
      copyCondition,
      errors,
      removeCondition,
      removeConditionByField,
      reset,
      strategy,
      updateCondition,
      updateStrategy,
      upsertConditionByField,
      validateConditions,
    ],
  );

  return (
    <SearchFiltersContext.Provider value={contextValue}>
      {children}
    </SearchFiltersContext.Provider>
  );
}

/** A hook exposing the context functionality. */
export function useSearchFilters(): SearchFiltersContextType {
  return useContext(SearchFiltersContext);
}
